import useQuestionnaireData from '@/hooks/use-questionnaire-data';
import { Box, Flex, ProgressBar, Stepper } from '@cmpsr/components';
import { FC } from 'react';
import { StepBarMinimal } from './StepBarMinimal';
import { type StepBarProps } from './types';
export const StepBar: FC<StepBarProps> = ({ steps, activeStep, userQuestionnaire }) => {
  const { currentSectionAssessmentProgress } = useQuestionnaireData(userQuestionnaire);
  const progress = currentSectionAssessmentProgress;

  if (steps.length === 1) {
    return <StepBarMinimal userQuestionnaire={userQuestionnaire} />;
  }

  return (
    <Box px={{ base: 10, lg: 16, xl: 24, xxl: 32 }} py={{ base: 3, lg: 4 }} maxW="1440px" w="100%" mx="auto">
      <Stepper index={activeStep} display="grid" gridTemplateColumns={`repeat(${steps.length}, 1fr)`}>
        {steps.map(({ id, name }, index) => (
          <Stepper.Step key={id} flexDirection="column" _horizontal={{ alignItems: 'start' }}>
            <Flex alignItems="center" width="100%">
              <ProgressBar
                variant="default"
                min={0}
                max={100}
                width="50%"
                height="6px"
                backgroundColor="cream-50"
                borderTopLeftRadius={index === 0 ? 'radii-2' : '0'}
                borderBottomLeftRadius={index === 0 ? 'radii-2' : '0'}
                value={_getValue({ index, activeStep, progress, isLeftSide: true })}
              />
              <Stepper.Step.Indicator>
                <Stepper.Step.Status
                  complete={<Stepper.Step.Number />}
                  incomplete={<Stepper.Step.Number />}
                  active={<Stepper.Step.Number />}
                />
              </Stepper.Step.Indicator>
              <ProgressBar
                variant="default"
                min={0}
                max={100}
                width="50%"
                height="6px"
                backgroundColor="cream-50"
                borderTopRightRadius={index === steps.length - 1 ? 'radii-2' : '0'}
                borderBottomRightRadius={index === steps.length - 1 ? 'radii-2' : '0'}
                value={_getValue({ index, activeStep, progress })}
              />
            </Flex>
            <Flex
              display={{ base: 'none', lg: 'flex' }}
              flexDirection="row"
              justifyContent="center"
              gap="spacer-1"
              flexShrink={0}
              width="full"
              mt={2}
            >
              <Stepper.Step.Title
                variant="text-body-floating-label-regular"
                color={activeStep >= index ? 'text-link-primary-default' : 'text-disabled'}
              >
                {name}
              </Stepper.Step.Title>
            </Flex>
          </Stepper.Step>
        ))}
      </Stepper>
    </Box>
  );
};

export function _getValue(args: { index: number; activeStep: number; progress: number; isLeftSide?: boolean }): number {
  const { index, activeStep, progress, isLeftSide } = args;
  // first progress bar, left side of the first step indicator
  if (isLeftSide && index === 0) {
    return 100;
  }

  //progress bars before the active step indicator
  if (index < activeStep) {
    return 100;
  }

  if (progress <= 50) {
    // progress bar next to the active step indicator, left side
    if (index === activeStep && isLeftSide) {
      return 100;
    }

    // progress bar next to the active step indicator, right side
    if (index === activeStep) {
      return progress * 2;
    }
  }

  if (progress > 50) {
    // progress bar next to the active step indicator, right side
    if (index === activeStep) {
      return 100;
    }

    // progress bar next to the next active step indicator, left side
    // this is the second half of the current progress bar
    if (index === activeStep + 1 && isLeftSide) {
      return (progress - 50) * 2;
    }
  }

  return 0;
}
